import * as appConfig from '../config';
import { resolveKryspaceReadSource } from '../config/kryspaceReadSource';
import type { KryspaceReadSource } from '../config/kryspaceReadSource';
import { apiClient } from './apiClient';
import type { KryspaceRenderManifest } from '../types/kryspaceRenderManifest';
import { isKryspaceRenderManifest } from '../types/kryspaceRenderManifest';

function readRenderConfig() {
  return {
    apiBaseUrl: appConfig.API_BASE_URL,
    kryspaceReadUrl: appConfig.KRYSPACE_READ_API_URL,
    devMock: appConfig.RENDER_SURFACE_DEV_MOCK,
  };
}

export type KryspaceRenderErrorCode =
  | 'NOT_CONFIGURED'
  | 'NOT_FOUND'
  | 'FORBIDDEN'
  | 'SERVICE_UNAVAILABLE'
  | 'INVALID_RESPONSE'
  | 'NETWORK';

export class KryspaceRenderServiceError extends Error {
  constructor(
    message: string,
    readonly code: KryspaceRenderErrorCode,
    readonly httpStatus?: number
  ) {
    super(message);
    this.name = 'KryspaceRenderServiceError';
  }
}

export interface FetchRenderManifestResult {
  manifest: KryspaceRenderManifest;
  /** True when served from REACT_APP_RENDER_SURFACE_DEV_MOCK (not live API). */
  devMock: boolean;
}

/** Phase B thin-read route (see cursor_frontend_kryspace_thin_read_api_plan_20260617.md). */
export type AttentionManifestQuery = {
  driveFilterScope?: string;
  drivePeriod?: string;
};

function appendDriveRailQuery(params: URLSearchParams, query?: AttentionManifestQuery): void {
  const scope = (query?.driveFilterScope || '').trim();
  const period = (query?.drivePeriod || '').trim();
  if (scope && scope !== 'company') {
    params.set('drive_filter_scope', scope);
  }
  if (period) {
    params.set('drive_period', period);
  }
}

function attentionManifestUrl(
  slug: string,
  source?: KryspaceReadSource,
  query?: AttentionManifestQuery
): string {
  const resolvedSource = source ?? resolveKryspaceReadSource({ workspaceSlug: slug });
  const params = new URLSearchParams({ source: resolvedSource });
  appendDriveRailQuery(params, query);
  return `/kryspace/workspace/${encodeURIComponent(slug)}/attention-manifest?${params.toString()}`;
}

/** Dev fallback when source=db fails (authZ/registry) but workspace disk read works. */
function attentionManifestUrls(
  slug: string,
  query?: AttentionManifestQuery,
  nodeEnv: string | undefined = process.env.NODE_ENV
): string[] {
  const primary = resolveKryspaceReadSource({ workspaceSlug: slug });
  const urls = [attentionManifestUrl(slug, primary, query)];
  if (nodeEnv !== 'production' && primary === 'db') {
    urls.push(attentionManifestUrl(slug, 'workspace', query));
  }
  return urls;
}

export function attentionManifestReadUrlsForTests(
  slug: string,
  nodeEnv: string | undefined
): string[] {
  return attentionManifestUrls(slug, undefined, nodeEnv);
}

/** Legacy Steve-lane path until backend router fully migrates. */
function legacyManifestUrl(slug: string): string {
  return `/company-model/${encodeURIComponent(slug)}/render-manifest`;
}

function buildDevMockManifest(workspaceSlug: string): KryspaceRenderManifest {
  const { kryspaceReadUrl, apiBaseUrl } = readRenderConfig();
  const now = new Date().toISOString();
  return {
    schemaVersion: 'render-manifest/v0.1',
    workspaceSlug,
    generatedAt: now,
    title: `${workspaceSlug} — attention surface (dev mock)`,
    readOnly: true,
    sections: [
      {
        id: 'rollup',
        title: 'Session rollup',
        kind: 'rollup',
        fields: [
          {
            key: 'posture',
            label: 'Posture',
            value: 'Read-only render surface (dev mock)',
            affordance: 'plain',
            tier: 'core',
            readOnly: true,
          },
          {
            key: 'api',
            label: 'Read API',
            value: kryspaceReadUrl || apiBaseUrl,
            affordance: 'plain',
            tier: 'context',
            readOnly: true,
          },
        ],
      },
      {
        id: 'attention-queue',
        title: 'Attention queue',
        kind: 'attention_queue',
        queue: [
          {
            itemId: 'co-pia-render-surface-frontend-readonly-build-20260607',
            title: 'Read-only manifest renderer on krysalis_business branch',
            statusPhrase: 'in progress',
            priority: 'P1',
            recommended: true,
          },
        ],
      },
    ],
  };
}

function messageFromDetail(data: unknown): { message?: string; code?: string } {
  if (!data || typeof data !== 'object') return {};
  const root = data as { detail?: unknown; message?: string; code?: string };
  if (typeof root.detail === 'string') {
    return { message: root.detail };
  }
  if (root.detail && typeof root.detail === 'object') {
    const d = root.detail as { message?: string; code?: string };
    return { message: d.message, code: d.code };
  }
  if (typeof root.message === 'string') {
    return { message: root.message, code: root.code };
  }
  return {};
}

function isRenderServiceError(err: unknown): err is KryspaceRenderServiceError {
  if (err instanceof KryspaceRenderServiceError) {
    return true;
  }
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as KryspaceRenderServiceError).name === 'KryspaceRenderServiceError' &&
    typeof (err as KryspaceRenderServiceError).code === 'string'
  );
}

/** Duck-type guard for render service errors (safe across bundle/test boundaries). */
export function isKryspaceRenderServiceError(err: unknown): err is KryspaceRenderServiceError {
  return isRenderServiceError(err);
}

/** Map axios-style API errors to typed render service errors (KRYBOT spec §5). */
export function mapRenderManifestHttpError(err: unknown): KryspaceRenderServiceError {
  if (isRenderServiceError(err)) {
    return err;
  }
  const ax = err as { response?: { status?: number; data?: unknown }; message?: string };
  const status = ax.response?.status;
  const { message: detailMessage, code: detailCode } = messageFromDetail(ax.response?.data);
  const fallback = ax.message || 'Failed to load render manifest';

  if (status === 403) {
    return new KryspaceRenderServiceError(
      detailMessage || 'Not allowed to read this kryspace workspace',
      detailCode === 'FORBIDDEN' ? 'FORBIDDEN' : 'FORBIDDEN',
      403
    );
  }
  if (status === 404) {
    return new KryspaceRenderServiceError(
      detailMessage || 'No render manifest for this workspace',
      'NOT_FOUND',
      404
    );
  }
  if (status === 503) {
    return new KryspaceRenderServiceError(
      detailMessage || 'Kryspace read API is unavailable',
      'SERVICE_UNAVAILABLE',
      503
    );
  }
  if (status === 502 || status === 504) {
    return new KryspaceRenderServiceError(
      'Control Bridge is still preparing this workspace. Wait for the loading state to finish, then retry — a hard refresh often succeeds once the server cache is warm.',
      'SERVICE_UNAVAILABLE',
      status
    );
  }
  if (status === 500) {
    return new KryspaceRenderServiceError(
      detailMessage || 'Workspace view could not be built. Try again.',
      'NETWORK',
      500
    );
  }
  if (status === 401) {
    return new KryspaceRenderServiceError(
      detailMessage || 'Authentication required',
      'FORBIDDEN',
      401
    );
  }
  const msg = String(fallback || '');
  if (/timeout/i.test(msg) || /exceeded/i.test(msg)) {
    return new KryspaceRenderServiceError(
      'Control Bridge is taking longer than usual to build. Keep this page open or tap Retry — the first load after a restart can be slow.',
      'SERVICE_UNAVAILABLE'
    );
  }
  return new KryspaceRenderServiceError(fallback, 'NETWORK', status);
}

/** Dev mock is only for missing config or transport failure — never mask 403/404 (spec §5). */
function mayUseDevMockFallback(err: unknown): boolean {
  const { devMock } = readRenderConfig();
  if (!devMock) return false;
  if (err instanceof KryspaceRenderServiceError) {
    return err.code === 'NOT_CONFIGURED' || err.code === 'NETWORK' || err.code === 'SERVICE_UNAVAILABLE';
  }
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === 403 || status === 404 || status === 401) return false;
  return true;
}

/** Override the 6-minute apiClient default so hung thin-reads surface eventually.
 * Hosted DDD cold derive can exceed 25s before CB doc warm finishes (Orlin A1). */
function timeoutForManifestUrl(url: string): number {
  return url.includes('source=db') ? 90000 : 12000;
}

async function fetchFromUrl(url: string, baseURL?: string): Promise<KryspaceRenderManifest> {
  try {
    const response = await apiClient.get<KryspaceRenderManifest>(url, {
      ...(baseURL ? { baseURL } : {}),
      timeout: timeoutForManifestUrl(url),
    });
    const data = response.data;
    if (!isKryspaceRenderManifest(data)) {
      throw new KryspaceRenderServiceError(
        'Render manifest response failed schema validation',
        'INVALID_RESPONSE'
      );
    }
    return data;
  } catch (err: unknown) {
    if (isRenderServiceError(err)) {
      throw err;
    }
    throw mapRenderManifestHttpError(err);
  }
}

/**
 * Fetch attention-surface manifest for a kryspace workspace slug.
 * Derivation rules live in kryspace; this client is a pure consumer.
 */
export async function fetchRenderManifest(
  workspaceSlug: string,
  query?: AttentionManifestQuery
): Promise<KryspaceRenderManifest> {
  const result = await fetchRenderManifestWithMeta(workspaceSlug, query);
  return result.manifest;
}

export async function fetchRenderManifestWithMeta(
  workspaceSlug: string,
  query?: AttentionManifestQuery
): Promise<FetchRenderManifestResult> {
  const { apiBaseUrl, kryspaceReadUrl, devMock } = readRenderConfig();
  const slug = workspaceSlug.trim();
  if (!slug) {
    throw new KryspaceRenderServiceError('Workspace slug is required', 'NOT_CONFIGURED');
  }

  const bases: { base: string; url: string; legacy?: boolean }[] = [];
  if (kryspaceReadUrl) {
    bases.push({ base: kryspaceReadUrl, url: legacyManifestUrl(slug), legacy: true });
  }
  if (apiBaseUrl) {
    bases.push({ base: apiBaseUrl.replace(/\/$/, ''), url: attentionManifestUrl(slug, undefined, query) });
  }

  if (bases.length === 0) {
    if (devMock) {
      return { manifest: buildDevMockManifest(slug), devMock: true };
    }
    throw new KryspaceRenderServiceError(
      'Kryspace read API is not configured. Set REACT_APP_API_URL or REACT_APP_KRYSPACE_READ_API_URL, or REACT_APP_RENDER_SURFACE_DEV_MOCK=true for local dev.',
      'NOT_CONFIGURED'
    );
  }

  let lastErr: KryspaceRenderServiceError | undefined;
  for (let i = 0; i < bases.length; i += 1) {
    const attempt = bases[i];
    const manifestUrls = attempt.legacy ? [attempt.url] : attentionManifestUrls(slug, query);
    for (let j = 0; j < manifestUrls.length; j += 1) {
      const manifestUrl = manifestUrls[j];
      try {
        const manifest = await fetchFromUrl(manifestUrl, attempt.base);
        return { manifest, devMock: false };
      } catch (err: unknown) {
        const serviceErr = isRenderServiceError(err) ? err : mapRenderManifestHttpError(err);
        lastErr = serviceErr;

        const hasAnotherManifestSource = j < manifestUrls.length - 1;
        // Prefer the next attention-manifest source (db → workspace) before failing hard.
        if (
          hasAnotherManifestSource &&
          (serviceErr.code === 'NETWORK' ||
            serviceErr.code === 'FORBIDDEN' ||
            serviceErr.code === 'NOT_FOUND' ||
            serviceErr.code === 'SERVICE_UNAVAILABLE')
        ) {
          continue;
        }

        const hasAnotherBase = i < bases.length - 1;
        if (serviceErr.code === 'NOT_FOUND' && !attempt.legacy && hasAnotherBase) {
          break;
        }

        if (
          serviceErr.code === 'FORBIDDEN' ||
          serviceErr.code === 'NOT_FOUND' ||
          serviceErr.code === 'INVALID_RESPONSE' ||
          serviceErr.code === 'SERVICE_UNAVAILABLE'
        ) {
          throw serviceErr;
        }

        if (!mayUseDevMockFallback(serviceErr)) {
          throw serviceErr;
        }
      }
    }
  }

  if (lastErr && mayUseDevMockFallback(lastErr)) {
    return { manifest: buildDevMockManifest(slug), devMock: true };
  }

  if (lastErr) {
    throw lastErr;
  }
  throw new KryspaceRenderServiceError('Failed to load render manifest', 'NETWORK');
}
